import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, getToken, hasAuthSession, getAuthHeaders } from '../../../../../Constant';
import axios from 'axios';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../../Components/Common/HeaderInfo';

function NetProfitTally() {
  const navigate = useNavigate();
  
  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    surveyYear: ''
  });

  // State for API data
  const [apiData, setApiData] = useState({
    expenses: { items: [], total: 0 },
    receipts: { items: [], total: 0 },
    net_profit: 0
  });
  const [loading, setLoading] = useState(false);

  // Ref to prevent duplicate API calls for same DSL+Year
  const apiCallMadeRef = useRef(false);

  // On page load: read header from localStorage and call net-profit-tally API immediately
  useEffect(() => {
    let dslNo = '';
    let pslNo = '';
    let companyName = '';
    let surveyYear = '';

    try {
      const balanceSheetData = localStorage.getItem('balanceSheetFormData');
      if (balanceSheetData) {
        const parsedData = JSON.parse(balanceSheetData);
        dslNo = parsedData.dslNo || '';
        pslNo = parsedData.pslNo || '';
        companyName = parsedData.nameUnit || '';
        surveyYear = parsedData.year || '';
      } else {
        dslNo = 'DSL No. not set';
        pslNo = 'PSL No. not set';
        companyName = 'Company name not set';
        surveyYear = 'Survey Year not set';
      }
      setHeaderData({ despatchSlNo: dslNo, permanentSlNo: pslNo, companyName, surveyYear });
    } catch (error) {
      console.error('Error fetching data from localStorage:', error);
      setHeaderData({
        despatchSlNo: 'Error loading data',
        permanentSlNo: 'Error loading data',
        companyName: 'Error loading data',
        surveyYear: 'Error loading data'
      });
      return;
    }

    // Skip API if DSL/Year not set (user never filled balance sheet)
    if (!dslNo || !surveyYear || dslNo === 'DSL No. not set' || surveyYear === 'Survey Year not set') {
      return;
    }

    const callKey = `${dslNo}-${surveyYear}`;
    if (apiCallMadeRef.current === callKey) return;
    apiCallMadeRef.current = callKey;

    const fetchApiData = async () => {
      try {
        setLoading(true);
        showLoading(true);

        if (!hasAuthSession()) {
          toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
          setLoading(false);
          showLoading(false);
          apiCallMadeRef.current = false;
          return;
        }

        const payload = { dsl_no: dslNo, survey_year: surveyYear };
        const response = await axios.post(
          `${API_URL}api/blockd/net-profit-tally`,
          payload,
          getAuthHeaders()
        );

        if (response.data.status) {
          setApiData(response.data.data || {
            expenses: { items: [], total: 0 },
            receipts: { items: [], total: 0 },
            net_profit: 0
          });
        } else {
          toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
        }
      } catch (error) {
        console.error('Error fetching API data:', error);
        if (error.response?.status === 401) {
          toast.error('Session expired. Please login again.', messages.COLOR_RED);
        } else if (error.response?.status === 404) {
          toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
        } else {
          toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
        }
        apiCallMadeRef.current = false;
      } finally {
        setLoading(false);
        showLoading(false);
      }
    };

    fetchApiData();
  }, []); // Run once when page loads
  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
     
      <div className="container-fluid py-3">
        {/* Header Section - using HeaderInfo component like InputItemsImported */}
        <HeaderInfo
          title="Net Profit Tally"
          icon="fa fa-calculator"
          iconColor="#1976d2"
          titleColor="#1976d2"
          backgroundColor=""
          dispatchSlNo={headerData.despatchSlNo}
          permanentSlNo={headerData.permanentSlNo}
          companyName={headerData.companyName}
          companyPlaceholder="Enter Company Name"
          showBlockCType={false}
          includeTableStyles={false}
        />

        {/* Main Content Area - Expenses left; Receipt + NET PROFIT right, same total height as Expenses */}
        <div className="row align-items-stretch">
          {/* Expenses - left card */}
          <div className="col-md-6">
            <div className="card mb-3 h-100">
              <div className="card-header">
                <h5 className="mb-0 pb-1 pt-1" style={{ fontWeight: 'bold' }}>Expenses</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead>
                      <tr>
                        <th >Sl no.</th>
                        <th >Item</th>
                        <th >Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="3" className="text-center">Loading...</td>
                        </tr>
                      ) : (
                        <>
                          {apiData.expenses.items.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{item.item_name}</td>
                              <td className="amount-cell">{item.amount.toLocaleString()}</td>
                            </tr>
                          ))}
                          <tr className="total-row">
                            <td colSpan="2">Total Expenses</td>
                            <td className="amount-cell">{apiData.expenses.total.toLocaleString()}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Receipt + NET PROFIT, total height = Expenses card height */}
          <div className="col-md-6 d-flex flex-column">
            <div className="card mb-2">
              <div className="card-header">
                <h5 className="mb-0 pb-1 pt-1" style={{ fontWeight: 'bold' }}>Receipt</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>Sl no.</th>
                        <th>Item</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="3" className="text-center">Loading...</td>
                        </tr>
                      ) : (
                        <>
                          {apiData.receipts.items.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{item.item_name}</td>
                              <td className="amount-cell">{item.amount.toLocaleString()}</td>
                            </tr>
                          ))}
                          <tr className="total-row">
                            <td colSpan="2">Total Income</td>
                            <td className="amount-cell">{apiData.receipts.total.toLocaleString()}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="card mb-3 mt-auto">
              <div className="card-body text-center py-3">
                <strong className="d-block" style={{ fontSize: '1.1rem' }}>
                  NET PROFIT = {loading ? 'Loading...' : (apiData.net_profit != null ? apiData.net_profit.toLocaleString() : '0')}
                </strong>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">

            <button
              type="button"
              className="btn btn_orange mx-1"
              // onClick={() => navigate('/validation')}
              onClick={() => navigate('/validation')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/part-a')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            {/* <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/final-checklist')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button> */}
          </div>
        </div>
      </div>
    </>
  );
}

export default NetProfitTally; 